import { connectionLabel } from '../../lib/connectionLabel';
import { useConnectionStatus } from '../../lib/connection';
import { Children, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useScrollEdges } from './scrollEdges';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Archive, Bug, Star, FolderTree, ChevronDown, ChevronRight, Hash, Lock, LogOut, Moon, Plus, Search, Settings, Sun, Users, Wifi, WifiOff, PanelLeftClose, Pin, UserRound, GripVertical, MoreHorizontal } from 'lucide-react';
import { canCreateInSection, isModuleOn, newChannelInLabel, orderLoose, resolveSidebarColour, type Conversation, type SidebarColours } from '@tradeoutlet/shared';
import { useLooseChannels } from '@/features/chat/looseChannels';
import { SidebarColourMenu, Stripe, type ColourMenuTarget } from '@/features/chat/SidebarColours';
import { ChannelOrderProvider, useChannelOrderState, useRowOrder, type OrderGroup } from '@/features/chat/channelOrder';
import { useAuth } from '@/lib/auth';
import { useNavAccess } from '@/lib/navAccess';
import { conversationName, useChatSections, useConversations, useMe, useMembers, useProjects, useWorkspace, useWorkspaceId } from '@/lib/hooks';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { usePermissions } from '@/lib/permissions';
import { buildSectionTree, type SectionNode } from '@/lib/sectionTree';
import { realtime } from '@/lib/realtime';
import { useUi } from '@/lib/store';
import { Avatar, CountPill, Menu, MenuItem } from '@/components/ui';
import { NewChannelModal, NewDmModal } from '@/features/chat/ConversationModals';
import { SectionsModal } from '@/features/chat/SectionsModal';
import { DmQuickSearch } from '@/features/chat/DmQuickSearch';
import { SidebarScheduledLink } from '@/features/chat/scheduled/SidebarScheduledLink';
import { SidebarFilterBox } from '@/features/chat/SidebarFilterBox';
import { filterConversations, matchPeople, moveHighlight, NO_MATCHES, normFilter, OTHER_CHANNELS_HEADING, otherChannels, PEOPLE_HEADING, pruneSections, type PersonResult } from '@/features/chat/sidebarFilter';
import { useOpenChannel } from '@/features/chat/joinChannel';
import { usePeopleDirectory } from './PeopleSuggest';
import { NewBuildingModal } from '@/features/buildings/NewBuildingModal';
import { BrandMark } from '@/components/brand/BrandMark';
import { NAV_ASSISTANT, NAV_PINNED, type AreaId, type NavItem } from '@/lib/navModel';
import { useNavArea, useNavBadges } from '@/lib/navArea';
import { holdsAnywhere } from '@/lib/navAccess';
import { useBuildings } from '@/features/buildings/common';
import { useNavFollows } from '@/lib/follows';
import { useBrand } from '@/lib/branding';
import { RoomIcon } from '@/features/rooms/RoomIcon';
import { REPORT_LABEL } from '@/features/feedback/label';
import { SECTION_TITLE as SHORTCUTS_TITLE, useShortcuts } from '@/features/shortcuts/shortcuts';
import { ShortcutRows } from '@/features/shortcuts/SidebarShortcuts';

/** One heading style for every sidebar group and chat category: small uppercase, semibold, letter-spaced, muted
 * (grey-400 on black, 7.9:1), with a collapse chevron; items sit indented beneath. */
const headingCls = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-sidebar-muted';

/** `top` (e.g. an inline search) shows under the heading even while the section is collapsed. */
function Section({ title, children, action, storageKey, top, divider, forceOpen, flush }: { title: string | null; children: React.ReactNode; action?: React.ReactNode; storageKey: string; top?: React.ReactNode; divider?: boolean;
  /** t674u: area page lists (e.g. People in People & shifts) line up with every other item; only channel/DM rows indent. */ flush?: boolean; /** t480u: while the sidebar is filtered, matches show even in a collapsed section. */ forceOpen?: boolean }) {
  const [stored, setOpen] = useState(() => localStorage.getItem(`collab.sb.${storageKey}`) !== 'closed');
  const open = stored || Boolean(forceOpen);
  // A section whose every item is hidden (e.g. by permissions) disappears entirely, header included.
  if (!action && !Children.toArray(children).length) return null;
  if (!title) return (
    <div className={divider ? 'mt-2' : 'mt-1'} data-testid={`nav-section-${storageKey}`}>
      {divider && <div className="mx-4 mb-2 border-t border-white/10" role="separator" data-testid="nav-divider" />}
      <div className="space-y-px">{children}</div>
    </div>
  );
  return (
    // Group headings match the Settings hub: 13px semibold, sentence case, full-contrast text, a hairline divider and
    // extra space above. Channel/DM rows indent beneath; area page lists don't (t674u).
    <div className="mx-2 mt-4 border-t border-white/10 pt-3" data-testid={`nav-section-${storageKey}`}>
      <div className={clsx('group flex items-center justify-between px-2', headingCls)} data-testid="nav-section-heading">
        <button type="button" aria-expanded={open} className={clsx('flex items-center gap-1 rounded py-0.5 hover:text-white', headingCls)} onClick={() => { setOpen(!stored); localStorage.setItem(`collab.sb.${storageKey}`, stored ? 'closed' : 'open'); }}>
          {open ? <ChevronDown className="h-3 w-3 shrink-0" aria-hidden /> : <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />}{title}
        </button>
        <span className="text-sidebar-muted">{action}</span>
      </div>
      {top && <div className="-mx-2 mt-1.5">{top}</div>}
      {open && <div className={clsx('-mx-2 mt-1.5 space-y-px', !flush && '[&>a]:pl-6')} data-flush={flush || undefined}>{children}</div>}
    </div>
  );
}

const itemCls = ({ isActive }: { isActive: boolean }) => clsx(
  'relative flex items-center gap-2 rounded-md mx-2 px-2 py-1 text-[13.5px] transition',
  // Active: raised black, white text and icon, medium weight, and a yellow indicator bar at the rail edge (never yellow text).
  isActive
    ? 'bg-sidebar-active font-medium text-white before:absolute before:inset-y-0.5 before:-left-2 before:w-1 before:rounded-r before:bg-sidebar-indicator'
    : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white',
);

/** t480u: the row Up/Down has reached while filtering (set on the element by Sidebar), calm like hover. */
const resultCls = 'data-[highlighted]:bg-sidebar-hover data-[highlighted]:text-white data-[highlighted]:ring-1 data-[highlighted]:ring-inset data-[highlighted]:ring-white/20';

type OpenColourMenu = (t: ColourMenuTarget) => void;
interface StripeProps { colours?: SidebarColours; onColourMenu?: OpenColourMenu; /** t446u: a section's + (New channel in that section). */ onNewChannel?: (sectionId: string) => void }

/** Collapsible section in the channel tree; open/closed state is remembered per section. */
function SectionBranch({ node, me, colours, onColourMenu, onNewChannel, filtering }: { node: SectionNode; me: string | null; /** t480u: the sidebar is filtered: open, no re-ordering, rows are results. */ filtering?: boolean } & StripeProps) {
  const key = `collab.sec.${node.section.id}`;
  const [stored, setOpen] = useState(() => localStorage.getItem(key) !== 'closed');
  const open = stored || Boolean(filtering);
  const toggle = () => { localStorage.setItem(key, stored ? 'closed' : 'open'); setOpen(!stored); };
  const colour = resolveSidebarColour(colours, 'section', node.section.id).colour;
  const group: OrderGroup = useMemo(() => ({ sectionId: node.section.id, ids: node.channels.map(c => c.id) }), [node.section.id, node.channels]);
  return (
    // The section's stripe runs down the whole block (header and its channels); a channel's own colour draws over it.
    <div className="relative" data-section-colour={colour?.id}>
      <Stripe hex={colour?.hex} className="left-2" />
      <div className="group/sec relative">
      <button onClick={toggle} title={node.section.name} aria-expanded={open}
        onContextMenu={onColourMenu && (e => { e.preventDefault(); onColourMenu({ type: 'section', id: node.section.id, name: node.section.name, x: e.clientX, y: e.clientY }); })}
        className={clsx('mx-2 mt-3 flex w-[calc(100%-1rem)] items-center gap-1 rounded-md px-2 pb-1 pt-1 text-left hover:bg-sidebar-hover hover:text-white', headingCls)}
        style={{ paddingLeft: 8 + node.depth * 12 }} data-testid="chat-category-heading">
        {open ? <ChevronDown className="h-3 w-3 shrink-0" aria-hidden /> : <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />}
        <span className="truncate">{node.section.name}</span>
        {!open && node.unread > 0 && <span className={clsx('ml-auto h-2 w-2 shrink-0 rounded-full bg-white', onNewChannel && 'mr-5')} title={`${node.unread} with unread messages`} />}
      </button>
      {/* t446u: like the Channels heading's +, it shows on hover or keyboard focus. */}
      {onNewChannel && (
        <button type="button" title={newChannelInLabel(node.section.name)} aria-label={newChannelInLabel(node.section.name)} data-testid="section-new-channel"
          onClick={() => onNewChannel(node.section.id)}
          className="absolute right-3 top-[calc(50%+6px)] -translate-y-1/2 rounded p-1 text-sidebar-muted opacity-0 transition hover:bg-sidebar-hover hover:text-white focus-visible:opacity-100 group-hover/sec:opacity-100">
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}
      </div>
      {open && (
        <div>
          {node.children.map(child => <SectionBranch key={child.section.id} node={child} me={me} colours={colours} onColourMenu={onColourMenu} onNewChannel={onNewChannel} filtering={filtering} />)}
          {node.channels.map(c => <ConversationLink key={c.id} c={c} me={me} indent={node.depth + 1} colours={colours} onColourMenu={onColourMenu} group={filtering ? undefined : group} result={filtering} />)}
        </div>
      )}
    </div>
  );
}

function ConversationLink({ c, me, indent = 0, colours, onColourMenu, group, result }: { c: Conversation; me: string | null; indent?: number; /** t446u: its group, for re-ordering. */ group?: OrderGroup; /** t480u: a result of the sidebar filter (Up/Down/Enter reach it). */ result?: boolean } & StripeProps) {
  const ord = useRowOrder(c.id, group);
  const presence = useUi(s => s.presence);
  const unread = c.unread_count > 0 && c.notify_level !== 'none';
  const other = c.kind === 'dm' ? c.members?.find(m => m.user_id !== me) : undefined;
  const colour = resolveSidebarColour(colours, 'conversation', c.id).colour;
  const name = conversationName(c, me);
  return (
    <NavLink to={`/c/${c.id}`} className={p => clsx(itemCls(p), 'group/row', resultCls, unread && !p.isActive && 'font-semibold text-white')} style={indent ? { paddingLeft: 12 + indent * 12 } : undefined}
      data-colour={colour?.id} data-filter-result={result || undefined} {...ord.drag}
      onContextMenu={onColourMenu && (e => { e.preventDefault(); onColourMenu({ type: 'conversation', id: c.id, name: c.kind === 'channel' ? `#${name}` : name, x: e.clientX, y: e.clientY, personalOnly: c.kind !== 'channel', order: ord.moves }); })}>
      <Stripe hex={colour?.hex} />
      {/* t446u: drag to re-order within the section; the grip shows on hover, a line marks where it will land. */}
      {ord.drag && <GripVertical aria-hidden data-testid="channel-drag-handle" className="absolute left-0.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 cursor-grab text-sidebar-muted opacity-0 transition group-hover/row:opacity-100" />}
      {ord.indicator && <span aria-hidden data-testid="channel-drop-line" className={clsx('pointer-events-none absolute inset-x-2 h-0.5 rounded bg-white', ord.indicator === 'before' ? '-top-px' : '-bottom-px')} />}
      {c.kind === 'channel'
        ? (c.is_private ? <Lock className="h-3.5 w-3.5 shrink-0 opacity-70" /> : <Hash className="h-3.5 w-3.5 shrink-0 opacity-70" />)
        : c.kind === 'room' ? <RoomIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
        : <Avatar size={18} name={other?.display_name ?? conversationName(c, me)} id={other?.user_id ?? c.id} presence={other ? presence[other.user_id] ?? 'offline' : undefined} />}
      <span className="truncate">{conversationName(c, me)}</span>
      {/* t272u: a little room on the right so the count pill never meets the scrollbar or the edge. */}
      <span className="ml-auto flex items-center gap-1 pr-1">
        {c.mention_count > 0 ? <CountPill n={c.mention_count} tone="red" /> : unread && c.kind !== 'channel' ? <CountPill n={c.unread_count} tone="red" /> : null}
      </span>
    </NavLink>
  );
}

/** `flyout` (t671u): the auto-hide panel next to the icon rail, which already shows the areas, so no area row here. */
export function Sidebar({ pinned = true, onTogglePin, flyout = false }: { pinned?: boolean; onTogglePin?: () => void; flyout?: boolean } = {}) {
  const { userId } = useAuth();
  const ws = useWorkspace();
  const convs = useConversations();
  const projects = useProjects();
  const nav = useNavigate();
  const [modal, setModal] = useState<null | 'channel' | 'dm' | 'sections'>(null);
  const [newBuilding, setNewBuilding] = useState(false);
  const [colourMenu, setColourMenu] = useState<ColourMenuTarget | null>(null);
  const [dmSearch, setDmSearch] = useState(false);
  const sections = useChatSections();
  const buildings = useBuildings();
  const followed = (buildings.data?.buildings ?? []).filter(b => b.following);
  const follows = useNavFollows();
  const followedTeams = follows.data?.teams ?? [];
  const followedPeople = follows.data?.people ?? [];
  // t607u: pages you saved from the top bar's bookmark; the Sidebar is desktop only, so this is too.
  const shortcuts = useShortcuts().data?.shortcuts ?? [];
  const presence = useUi(s => s.presence);
  const loc = useLocation();
  const connStatus = useConnectionStatus();
  const connLabel = connectionLabel(connStatus);

  const { areas, area, selected, open: openArea } = useNavArea();
  const list = convs.data?.conversations ?? [];
  // t480u: "Find a channel or person" beside the Chat heading filters everything below it as you type.
  const [filter, setFilter] = useState('');
  const filtering = selected === 'chat' && normFilter(filter) !== '';
  const named = (c: Conversation) => conversationName(c, userId);
  const channels = useMemo(() => {
    const all = (convs.data?.conversations ?? []).filter(c => c.kind === 'channel');
    return filtering ? filterConversations(all, filter, c => conversationName(c, userId)) : all;
  }, [convs.data, filtering, filter, userId]);
  const tree = useMemo(() => {
    const t = buildSectionTree(sections.data?.sections ?? [], channels);
    return filtering ? { ...t, roots: pruneSections(t.roots) } : t;
  }, [sections.data, channels, filtering]);
  const rooms = filterConversations(list.filter(c => c.kind === 'room'), filtering ? filter : '', named);
  const dms = filterConversations(list.filter(c => c.kind !== 'channel' && c.kind !== 'room'), filtering ? filter : '', named);
  const openNewRoom = useUi(s => s.setNewRoomOpen);
  const colours = sections.data?.colours;
  const mine = usePermissions().data;
  const anywhere = (...perms: string[]) => Boolean(mine && perms.some(p => mine.workspace.includes(p) || mine.scoped.some(s => s.permissions.includes(p))));
  const { can } = useNavAccess();
  const loose = useLooseChannels();
  const channelOrder = useChannelOrderState();
  const looseGroup: OrderGroup = useMemo(() => ({ sectionId: null, ids: tree.loose.map(c => c.id) }), [tree.loose]);
  const [newChannelSection, setNewChannelSection] = useState<string | null>(null);
  const newInSection = canCreateInSection(anywhere('chat.channel.create'), sections.data?.can_manage)
    ? (id: string) => { setNewChannelSection(id); setModal('channel'); } : undefined;
  const openPerson = useUi(s => s.openPerson);
  const badges = useNavBadges();
  const pinnedItems = usePinnedItems();
  const brand = useBrand();
  // t272u: a soft fade where the list runs under the fixed header or footer, only while there's more to scroll to.
  const navRef = useRef<HTMLElement>(null);
  const edges = useScrollEdges(navRef);
  // t480u: people you could message and public channels you could join, fetched only once something is typed.
  const wsId = useWorkspaceId();
  const { members } = useMembers();
  const directory = usePeopleDirectory(filtering);
  const browse = useQuery({ queryKey: ['browse', wsId, ''], queryFn: () => api.browseChannels(wsId!), enabled: Boolean(wsId) && filtering, staleTime: 60_000 });
  const shownDmWith = useMemo(() => new Set(dms.filter(c => c.kind === 'dm').flatMap(c => c.members?.filter(m => m.user_id !== userId).map(m => m.user_id) ?? [])), [dms, userId]);
  const people = filtering ? matchPeople(members, directory.data?.people ?? [], list, userId, filter, shownDmWith) : [];
  const others = filtering ? otherChannels(browse.data?.channels ?? [], list, filter) : [];
  const matchCount = channels.length + rooms.length + dms.length + people.length + others.length;
  const toast = useUi(s => s.toast);
  const openChannel = useOpenChannel();
  const startDm = async (r: PersonResult) => {
    if (r.dmId) { nav(`/c/${r.dmId}`); return; }
    if (!wsId) return;
    // The same flow as New message and the DM quick search: POST /dms opens the existing 1:1 or creates it.
    try {
      const c = await api.openDm(wsId, [r.member.user_id]);
      void queryClient.invalidateQueries({ queryKey: qk.conversations(wsId) });
      nav(`/c/${c.id}`);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const filterInput = useRef<HTMLInputElement>(null);
  const resultsId = useId();
  const [hi, setHi] = useState(0);
  const results = () => [...(navRef.current?.querySelectorAll<HTMLElement>('[data-filter-result]') ?? [])];
  const changeFilter = (v: string) => { setFilter(v); setHi(0); navRef.current?.scrollTo?.({ top: 0 }); };
  const onFilterKey = (key: 'ArrowUp' | 'ArrowDown' | 'Enter') => {
    const els = results();
    if (key === 'Enter') { els[Math.min(hi, els.length - 1)]?.click(); return; }
    setHi(h => moveHighlight(Math.min(h, els.length - 1), els.length, key));
  };
  // Mark the highlighted result on the element itself (rows live in several sections), and keep it in view.
  useLayoutEffect(() => {
    const els = results();
    els.forEach((el, i) => {
      el.id = `${resultsId}-r${i}`;
      if (filtering && i === Math.min(hi, els.length - 1)) { el.setAttribute('data-highlighted', ''); el.scrollIntoView?.({ block: 'nearest' }); }
      else el.removeAttribute('data-highlighted');
    });
  });
  const activeId = filtering && matchCount ? `${resultsId}-r${Math.min(hi, matchCount - 1)}` : undefined;
  return (
    <aside className="on-dark flex h-full w-64 shrink-0 flex-col bg-sidebar text-white dark:border-r dark:border-white/10">
      <div className="flex h-12 items-center gap-2 border-b border-white/10 px-4">
        {/* Logo slot: see components/brand/BrandMark.tsx and docs/branding.md */}
        {/* Just the logo: it goes Home; the workspace name is in the tooltip (there's one workspace, so no switcher here). */}
        <Link to="/" aria-label={`${brand.title}, go to Home`} title={ws?.name ? `${ws.name} · go to Home` : 'Go to Home'} data-testid="sidebar-logo"
          className="shrink-0 rounded focus-visible:outline-2 focus-visible:outline-white">
          <BrandMark height={32} />
        </Link>
        <div className="flex-1" />
        <span title={connLabel} aria-label={connLabel} role="status" data-testid="sidebar-connection" data-status={connStatus}>
          {connStatus === 'online'
            ? <Wifi className={clsx('h-4 w-4', realtime.mode === 'realtime' ? 'text-emerald-400' : 'text-orange-300')} aria-hidden />
            : <WifiOff className={clsx('h-4 w-4', connStatus === 'offline' ? 'text-rose-400' : 'text-amber-300')} aria-hidden />}
        </span>
        {onTogglePin && (
          <button type="button" onClick={onTogglePin} data-testid="sidebar-pin" aria-pressed={pinned}
            title={pinned ? 'Unpin sidebar - collapse to icons (Ctrl+\\)' : 'Pin sidebar open'} aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
            className="rounded p-1 text-sidebar-muted hover:bg-sidebar-hover hover:text-white">
            {pinned ? <PanelLeftClose className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Backlog #53: a slim row of area icons; the list below shows only the selected area. Alt+1…5 switches. */}
      {!flyout && <AreaRow areas={areas} selected={selected} badge={badges.area} onOpen={openArea} />}

      <div className="relative flex min-h-0 flex-1 flex-col">
      <div aria-hidden data-testid="sidebar-fade-top" className={clsx('pointer-events-none absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b from-sidebar to-transparent transition-opacity duration-150', edges.top ? 'opacity-100' : 'opacity-0')} />
      <nav ref={navRef} id={resultsId} className="scroll-thin flex-1 overflow-y-auto pb-4 [scrollbar-gutter:stable]" aria-label="Main" data-testid="sidebar-area-nav" data-area={selected} data-more-below={edges.bottom || undefined}
        data-filtering={filtering || undefined}
        // t480u: opening a result (click or Enter) clears the box so the full list is back.
        onClickCapture={e => { if (filtering && (e.target as Element).closest?.('[data-filter-result]')) setFilter(''); }}>
        <div className="flex items-center gap-2 px-4 pb-1 pt-3">
          <div className="shrink-0 text-[15px] font-bold text-white" data-testid="sidebar-area-title">{area?.label}</div>
          {selected === 'chat' && <SidebarFilterBox value={filter} onChange={changeFilter} onNavKey={onFilterKey} activeId={activeId} resultsId={resultsId} inputRef={filterInput} />}
        </div>
        {area?.groups.map(g => (
          <Section key={g.id} title={g.title} storageKey={g.id} divider={g.divider} flush>
            {g.items.filter(i => can(i.to)).flatMap(i => [
              i.to === '/buildings' && anywhere('buildings.manage') ? (
                <div key={i.to} className="group relative" data-testid="sidebar-buildings-row">
                  <ItemLink item={i} count={badges.item(i.to)} />
                  <button type="button" title="New building" aria-label="New building" data-testid="sidebar-new-building" onClick={() => setNewBuilding(true)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-sidebar-muted opacity-0 transition hover:bg-sidebar-hover hover:text-white focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"><Plus className="h-3.5 w-3.5" /></button>
                </div>
              ) : <ItemLink key={i.to} item={i} count={badges.item(i.to)} />,
              ...(i.to === '/buildings' ? followed.map(b => (
                <NavLink key={b.id} to={`/buildings/${b.id}`} className={itemCls} style={{ paddingLeft: 32 }}>
                  <Star className="h-3 w-3 shrink-0 fill-accent text-black" strokeWidth={1.5} /><span className="truncate">{b.name}</span>
                  {b.open_task_count > 0 && <span className="ml-auto text-[11px] text-sidebar-muted">{b.open_task_count}</span>}
                </NavLink>
              )) : []),
              // t139: followed departments, groups and people sit under their item, like followed buildings.
              ...(i.to === '/departments' || i.to === '/custom-groups' ? followedTeams.filter(t => (t.kind === 'group') === (i.to === '/custom-groups')).map(t => (
                <NavLink key={t.id} to={`${i.to}?team=${t.id}`} className={() => itemCls({ isActive: loc.pathname === i.to && new URLSearchParams(loc.search).get('team') === t.id })}
                  style={{ paddingLeft: 32 }} data-testid="sidebar-followed-team">
                  <Star className="h-3 w-3 shrink-0 fill-accent text-black" strokeWidth={1.5} /><span className="truncate">{t.name}</span>
                </NavLink>
              )) : []),
              ...(i.to === '/people' ? followedPeople.map(pp => (
                <button key={pp.user_id} type="button" onClick={() => openPerson(pp.user_id)} data-testid="sidebar-followed-person" title={`View ${pp.display_name}’s profile`}
                  className={clsx(itemCls({ isActive: false }), 'w-[calc(100%-1rem)] text-left')} style={{ paddingLeft: 32 }}>
                  <Avatar size={16} name={pp.display_name} id={pp.user_id} presence={presence[pp.user_id] ?? 'offline'} /><span className="truncate">{pp.display_name}</span>
                </button>
              )) : []),
            ])}
          </Section>
        ))}

        {/* t607u: My Shortcuts, at the bottom of Home, in your order; hidden entirely while you have none. */}
        {selected === 'home' && shortcuts.length > 0 && (
          <Section title={SHORTCUTS_TITLE} storageKey="shortcuts">
            <ShortcutRows list={shortcuts} itemCls={itemCls} />
          </Section>
        )}

        {selected === 'chat' && (<>
        {(!filtering || channels.length > 0) && <Section title="Channels" storageKey="channels" forceOpen={filtering} action={
          <span className="flex items-center gap-1">
            <span className="flex gap-1 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
              {sections.data?.can_manage && <button type="button" title="Organise sections" aria-label="Organise sections" className="hover:text-white" onClick={() => setModal('sections')}><FolderTree className="h-3.5 w-3.5" /></button>}
              {/* t724u: Browse channels, beside New channel. */}
              <Link to="/channels" title="Browse channels" aria-label="Browse channels" data-testid="sidebar-browse-channels-icon" className="hover:text-white"><Hash className="h-3.5 w-3.5" /></Link>
              {anywhere('chat.channel.create') && <button type="button" title="New channel" aria-label="New channel" className="hover:text-white" onClick={() => setModal('channel')}><Plus className="h-3.5 w-3.5" /></button>}
            </span>
          </span>}>
          {/* t446u: channels without a section go above or below the sections (workspace default, or your own choice). */}
          <ChannelOrderProvider value={channelOrder}>
          {orderLoose(tree.roots, tree.loose, loose.position).map(part => part.kind === 'section'
            ? <SectionBranch key={part.item.section.id} node={part.item} me={userId} colours={colours} onColourMenu={setColourMenu} onNewChannel={newInSection} filtering={filtering} />
            : <div key="loose" data-testid="sidebar-loose-channels" data-position={loose.position}>{part.items.map(c => <ConversationLink key={c.id} c={c} me={userId} colours={colours} onColourMenu={setColourMenu} group={filtering ? undefined : looseGroup} result={filtering} />)}</div>)}
          </ChannelOrderProvider>
          {/* t724u: a direct way into Browse channels at the end of the list (it was only under search results). */}
          {!filtering && <div><NavLink to="/channels" end data-testid="sidebar-browse-channels" className={p => clsx('mx-2 flex items-center gap-2 rounded-md py-1 pl-6 pr-2 text-[13px]', p.isActive ? 'bg-sidebar-active text-white' : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-white')}><Hash className="h-3.5 w-3.5" />Browse channels</NavLink></div>}
        </Section>}

        {(!filtering || rooms.length > 0) && <Section title="Ad-hoc Meeting Rooms" storageKey="rooms" forceOpen={filtering} action={anywhere('chat.room.create') ?
          <button type="button" title="New ad-hoc meeting room" aria-label="New ad-hoc meeting room" data-testid="sidebar-new-room" className="-m-1 flex rounded p-1 hover:bg-sidebar-hover hover:text-white" onClick={() => openNewRoom(true)}><Plus className="h-3.5 w-3.5" /></button> : undefined}>
          {rooms.map(c => <ConversationLink key={c.id} c={c} me={userId} colours={colours} onColourMenu={setColourMenu} result={filtering} />)}
          {!filtering && <div><NavLink to="/meeting-rooms/closed" data-testid="sidebar-closed-rooms" className={p => clsx('mx-2 flex items-center gap-2 rounded-md py-1 pl-6 pr-2 text-[13px]', p.isActive ? 'bg-sidebar-active text-white' : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-white')}><Archive className="h-3.5 w-3.5" />Closed ad-hoc meeting rooms</NavLink></div>}
        </Section>}

        {(!filtering || dms.length > 0) && <Section title="Direct messages" storageKey="dms" forceOpen={filtering} action={
          <button type="button" title="New message" aria-label="New message" className="opacity-0 transition hover:text-white group-hover:opacity-100 group-focus-within:opacity-100" onClick={() => setModal('dm')}><Plus className="h-3.5 w-3.5" /></button>}>
          {dms.map(c => <ConversationLink key={c.id} c={c} me={userId} colours={colours} onColourMenu={setColourMenu} result={filtering} />)}
          {!filtering && <div className="mx-2 flex items-center gap-1">
            <button type="button" onClick={() => setModal('dm')} data-testid="sidebar-new-message" className="flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 pl-6 pr-2 text-[13px] text-sidebar-muted hover:bg-sidebar-hover hover:text-white"><Users className="h-3.5 w-3.5" />New message</button>
            <button type="button" onClick={() => setDmSearch(v => !v)} aria-expanded={dmSearch} aria-label="Find a person to message" title="Find a person to message" data-testid="dm-search-toggle"
              className={clsx('rounded-md p-1.5 hover:bg-sidebar-hover hover:text-white', dmSearch ? 'bg-sidebar-active text-white' : 'text-sidebar-muted')}><Search className="h-3.5 w-3.5" /></button>
          </div>}
          {!filtering && dmSearch && <div className="pb-1 pt-1"><DmQuickSearch onClose={() => setDmSearch(false)} /></div>}
          {!filtering && <SidebarScheduledLink />}
        </Section>}

        {filtering && people.length > 0 && (
          <FilterGroup title={PEOPLE_HEADING} testId="sidebar-filter-people">
            {people.map(r => (
              <button key={r.member.user_id} type="button" data-filter-result data-testid="sidebar-filter-person" onClick={() => void startDm(r)}
                title={r.dmId ? `Open your messages with ${r.member.display_name}` : `Message ${r.member.display_name}`}
                className={clsx(itemCls({ isActive: false }), resultCls, 'w-[calc(100%-1rem)] text-left')}>
                <Avatar size={20} name={r.member.display_name} id={r.member.user_id} presence={presence[r.member.user_id] ?? 'offline'} />
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate">{r.member.display_name}</span>
                  {r.detail && <span className="block truncate text-[11.5px] text-sidebar-muted">{r.detail}</span>}
                </span>
              </button>
            ))}
          </FilterGroup>
        )}
        {filtering && others.length > 0 && (
          <FilterGroup title={OTHER_CHANNELS_HEADING} testId="sidebar-filter-other-channels">
            {others.map(c => (
              <button key={c.id} type="button" data-filter-result data-testid="sidebar-filter-other-channel" onClick={() => void openChannel(c, false)}
                aria-label={`Join #${c.slug}`} title={`Join #${c.slug}${c.member_count ? ` (${c.member_count} members)` : ''}`}
                className={clsx(itemCls({ isActive: false }), resultCls, 'w-[calc(100%-1rem)] text-left')}>
                <Hash className="h-3.5 w-3.5 shrink-0 opacity-70" />
                <span className="truncate">{c.slug}</span>
                <span className="ml-auto shrink-0 pr-1 text-[11.5px] text-sidebar-muted">Join</span>
              </button>
            ))}
          </FilterGroup>
        )}
        {filtering && (
          <div className="mx-4 mt-3 space-y-2 text-[12px] text-sidebar-muted">
            {matchCount === 0 && <p data-testid="sidebar-filter-empty">{NO_MATCHES}</p>}
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <Link to={`/channels?q=${encodeURIComponent(filter.trim())}`} onClick={() => setFilter('')} className="rounded hover:text-white hover:underline" data-testid="sidebar-filter-browse">Browse all channels</Link>
              {anywhere('chat.channel.create') && <button type="button" onClick={() => { setFilter(''); setModal('channel'); }} className="rounded hover:text-white hover:underline" data-testid="sidebar-filter-new-channel">New channel</button>}
            </div>
          </div>
        )}
        {filtering && <p className="sr-only" role="status" aria-live="polite">{matchCount === 0 ? NO_MATCHES : matchCount === 1 ? '1 result' : `${matchCount} results`}</p>}
        </>)}

        {selected === 'tasks' && can('/projects') && (
          <Section title="Your projects" storageKey="projects">
            {(projects.data?.projects ?? []).filter(p => !p.archived_at).slice(0, 12).map(p => (
              <NavLink key={p.id} to={`/projects/${p.id}`} className={itemCls}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: p.color }} />
                <span className="truncate">{p.name}</span>
                {p.open_task_count ? <span className="ml-auto text-[11px] text-sidebar-muted">{p.open_task_count}</span> : null}
              </NavLink>
            ))}
          </Section>
        )}
      </nav>
      <div aria-hidden data-testid="sidebar-fade-bottom" className={clsx('pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-sidebar to-transparent transition-opacity duration-150', edges.bottom ? 'opacity-100' : 'opacity-0')} />
      </div>

      {/* Always here, whichever area is selected. t272u: the same hairline as the section dividers, even padding above and
          below, and links indented like the channel rows (icons at the same 32px). The rows use a 6px icon gap and no right
          padding so "Report Bug / Suggest Feature" shows in full on one line in the 256px sidebar. */}
      <div className="space-y-px border-t border-white/10 py-2 [&>a]:gap-1.5 [&>a]:pl-6 [&>a]:pr-0 [&>button]:gap-1.5 [&>button]:pl-6 [&>button]:pr-0" data-testid="sidebar-pinned">
        {pinnedItems.map(i => <PinnedEntry key={i.to} item={i} />)}
        {!pinnedItems.some(i => i.to === '/help') && <ReportLink />}
      </div>
      <div className="border-t border-white/10 p-2">
        <UserFooter settingsPinned={pinnedItems.some(i => i.to === '/settings')} />
      </div>

      <NewChannelModal open={modal === 'channel'} sectionId={newChannelSection} onClose={() => { setModal(null); setNewChannelSection(null); }} />
      <NewBuildingModal open={newBuilding} onClose={() => setNewBuilding(false)} onCreated={b => nav(`/buildings/${b.id}`)} />
      <NewDmModal open={modal === 'dm'} onClose={() => setModal(null)} />
      {modal === 'sections' && <SectionsModal open onClose={() => setModal(null)} />}
      {colourMenu && <SidebarColourMenu key={`${colourMenu.type}:${colourMenu.id}`} target={colourMenu} onClose={() => setColourMenu(null)} />}
    </aside>
  );
}

/** t480u: a group of extra results while filtering (People, Other channels), under a muted heading like the sections'. */
function FilterGroup({ title, testId, children }: { title: string; testId: string; children: React.ReactNode }) {
  return (
    <div className="mx-2 mt-4 border-t border-white/10 pt-3" data-testid={testId}>
      <div className={clsx('px-2 py-0.5', headingCls)} role="heading" aria-level={3}>{title}</div>
      <div className="-mx-2 mt-1.5 space-y-px [&>button]:pl-6">{children}</div>
    </div>
  );
}

const itemTestId = (to: string) => `nav-${to === '/' ? 'home' : to.replace(/^\//, '').replace(/\//g, '-')}`;

function ItemLink({ item: i, count }: { item: NavItem; count: number }) {
  return (
    <NavLink to={i.to} end={i.end} className={itemCls} data-testid={itemTestId(i.to)}>
      <i.icon className="h-4 w-4" /><span className="truncate">{i.label}</span>
      {count > 0 && <span className="ml-auto pr-1"><CountPill n={count} /></span>}
    </NavLink>
  );
}

/** Assistant (when you can use it), Help Centre and Settings. */
function usePinnedItems(): NavItem[] {
  const { can } = useNavAccess();
  const mine = usePermissions().data;
  const assistant = isModuleOn(mine?.modules_off, 'assistant') && (can(NAV_ASSISTANT.to) || holdsAnywhere(mine, 'ai.assistant.use'));
  return NAV_PINNED.filter(i => (i === NAV_ASSISTANT ? assistant : can(i.to)));
}

/** A pinned item, with "Report Bug / Suggest Feature" straight after Help so it's on every page (t171u). */
function PinnedEntry({ item, compact }: { item: NavItem; compact?: boolean }) {
  return <><PinnedLink item={item} compact={compact} />{item.to === '/help' && <ReportLink compact={compact} />}</>;
}

/** Opens the report dialog over the current page, which it captures. */
export function ReportLink({ compact }: { compact?: boolean }) {
  const openFeedback = useUi(s => s.openFeedback);
  const label = REPORT_LABEL;
  return (
    <button type="button" className={compact ? railCls({ isActive: false }) : clsx(itemCls({ isActive: false }), 'w-[calc(100%-1rem)] text-left')} data-testid="nav-report"
      title={compact ? label : undefined} aria-label={compact ? label : undefined} onClick={() => openFeedback(true)}>
      <Bug className="h-4 w-4" />{!compact && <span className="truncate">{label}</span>}
    </button>
  );
}

/** The assistant opens as a side panel rather than a page, so its pinned entry is a toggle. */
function PinnedLink({ item: i, compact }: { item: NavItem; compact?: boolean }) {
  const assistantOpen = useUi(s => s.assistantOpen);
  const openAssistant = useUi(s => s.openAssistant);
  const closeAssistant = useUi(s => s.closeAssistant);
  const id = itemTestId(i.to);
  if (i === NAV_ASSISTANT) {
    const cls = compact ? railCls({ isActive: assistantOpen }) : clsx(itemCls({ isActive: assistantOpen }), 'w-[calc(100%-1rem)] text-left');
    return (
      <button type="button" className={cls} data-testid={id} aria-pressed={assistantOpen} title={compact ? i.label : undefined} aria-label={compact ? i.label : undefined}
        onClick={() => (assistantOpen ? closeAssistant() : openAssistant())}>
        <i.icon className="h-4 w-4" />{!compact && i.label}
      </button>
    );
  }
  return <NavLink to={i.to} className={compact ? railCls : itemCls} data-testid={id} title={compact ? i.label : undefined} aria-label={compact ? i.label : undefined}><i.icon className="h-4 w-4" />{!compact && i.label}</NavLink>;
}

type VisibleArea = ReturnType<typeof useNavArea>['areas'][number];

/** One area button: icon, a yellow count badge (black text) and a yellow bar under the selected one. */
function AreaButton({ a, active, n, vertical, onOpen }: { a: VisibleArea; active: boolean; n: number; vertical?: boolean; onOpen: (id: AreaId) => void }) {
  const hint = `${a.label} (Alt+${a.key})${n ? ` - ${n} waiting` : ''}`;
  return (
    <button type="button" onClick={() => onOpen(a.id)} title={hint} aria-label={hint} aria-current={active ? 'page' : undefined}
      data-testid={`area-${a.id}`} data-active={active || undefined}
      className={clsx('relative flex h-9 items-center justify-center rounded-md transition', vertical ? 'w-9' : 'flex-1',
        active ? 'bg-sidebar-active text-white' : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white',
        active && (vertical ? 'before:absolute before:inset-y-1 before:-left-2.5 before:w-1 before:rounded-r before:bg-sidebar-indicator' : 'before:absolute before:inset-x-2 before:-bottom-1 before:h-0.5 before:rounded before:bg-sidebar-indicator'))}>
      <a.icon className="h-[18px] w-[18px]" />
      {n > 0 && <span className="absolute -right-0.5 -top-1"><CountPill n={n} /></span>}
    </button>
  );
}

function AreaRow({ areas, selected, badge, onOpen }: { areas: VisibleArea[]; selected: AreaId; badge: (id: AreaId) => number; onOpen: (id: AreaId) => void }) {
  return (
    <div className="flex items-center gap-1 border-b border-white/10 px-2 pb-2 pt-2" role="toolbar" aria-label="Areas" data-testid="area-row">
      {areas.map(a => <AreaButton key={a.id} a={a} active={a.id === selected} n={badge(a.id)} onOpen={onOpen} />)}
    </div>
  );
}

const railCls = ({ isActive }: { isActive: boolean }) => clsx('flex h-9 w-9 items-center justify-center rounded-md text-sidebar-fg hover:bg-sidebar-hover hover:text-white', isActive && 'bg-sidebar-active text-white');

/** Collapsed rail shown when the sidebar is unpinned: the area icons stacked, then the pinned items. Hover (or Ctrl+\) slides the full sidebar over. */
export function SidebarRail({ onExpand }: { onExpand: () => void }) {
  const { areas, selected, open } = useNavArea();
  const badges = useNavBadges();
  const pinnedItems = usePinnedItems();
  return (
    <nav className="on-dark flex h-full w-14 flex-col items-center gap-1 bg-sidebar py-2 text-white dark:border-r dark:border-white/10" aria-label="Main (collapsed)" data-testid="sidebar-rail">
      <button type="button" onClick={onExpand} className="mb-2 rounded p-1 hover:bg-sidebar-hover" aria-label="Show sidebar (Ctrl+\\)" title="Show sidebar (Ctrl+\\)">
        <BrandMark height={24} compact />
      </button>
      <div className="flex flex-col items-center gap-1.5" role="toolbar" aria-orientation="vertical" aria-label="Areas">
        {areas.map(a => <AreaButton key={a.id} a={a} active={a.id === selected} n={badges.area(a.id)} vertical onOpen={open} />)}
      </div>
      <div className="mt-auto flex flex-col items-center gap-1 border-t border-white/10 pt-2" data-testid="rail-pinned">
        {pinnedItems.map(i => <PinnedEntry key={i.to} item={i} compact />)}
        {!pinnedItems.some(i => i.to === '/help') && <ReportLink compact />}
        <RailUser />
      </div>
    </nav>
  );
}

/**
 * t672u: the name at the bottom of the sidebar opens your profile. Its small ⋯ holds only what isn't already in the
 * sidebar above it (Help, Report Bug / Suggest Feature and Settings are there): Dark mode and Sign out, plus Settings
 * only if it isn't pinned for you.
 */
function UserFooter({ settingsPinned }: { settingsPinned: boolean }) {
  const { userId, signOut } = useAuth();
  const profile = useMe().data?.profile;
  const openPerson = useUi(s => s.openPerson);
  const { theme, toggleTheme } = useUi();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => openPerson(userId)} title="My profile" data-testid="sidebar-me"
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-1.5 text-left hover:bg-sidebar-hover">
        <Avatar size={30} name={profile?.display_name} id={profile?.user_id} presence="online" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{profile?.display_name ?? '…'}</span>
          <span className="block truncate text-xs text-sidebar-muted">{profile?.status_text || profile?.job_title || 'Active'}</span>
        </span>
      </button>
      <Menu open={menu} setOpen={setMenu} align="right" up trigger={
        <button type="button" aria-label="More: dark mode, sign out" title="Dark mode, sign out" aria-expanded={menu} data-testid="sidebar-me-more"
          className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-muted hover:bg-sidebar-hover hover:text-white">
          <MoreHorizontal className="h-4 w-4" />
        </button>}>
        {!settingsPinned && <MenuItem icon={<Settings className="h-4 w-4" />} onClick={() => { setMenu(false); nav('/settings'); }}>Settings</MenuItem>}
        <MenuItem data-testid="menu-theme" icon={theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} onClick={() => { toggleTheme(); setMenu(false); }}>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</MenuItem>
        <MenuItem data-testid="menu-signout" danger icon={<LogOut className="h-4 w-4" />} onClick={() => { setMenu(false); void signOut(); }}>Sign out</MenuItem>
      </Menu>
    </div>
  );
}

/** t672u: the collapsed rail's own way to your profile, Dark mode and Sign out, so they never need the flyout. */
function RailUser() {
  const { userId, signOut } = useAuth();
  const profile = useMe().data?.profile;
  const openPerson = useUi(s => s.openPerson);
  const { theme, toggleTheme } = useUi();
  const [menu, setMenu] = useState(false);
  return (
    <Menu open={menu} setOpen={setMenu} align="left" up trigger={
      <button type="button" aria-label={`You: ${profile?.display_name ?? ''}`.trim()} title={profile?.display_name ?? 'You'} aria-expanded={menu} data-testid="rail-me"
        className="mt-1 flex h-9 w-9 items-center justify-center rounded-md hover:bg-sidebar-hover">
        <Avatar size={26} name={profile?.display_name} id={profile?.user_id} presence="online" />
      </button>}>
      <MenuItem icon={<UserRound className="h-4 w-4" />} onClick={() => { setMenu(false); openPerson(userId); }}>My profile</MenuItem>
      <MenuItem icon={theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} onClick={() => { toggleTheme(); setMenu(false); }}>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</MenuItem>
      <MenuItem data-testid="rail-signout" danger icon={<LogOut className="h-4 w-4" />} onClick={() => { setMenu(false); void signOut(); }}>Sign out</MenuItem>
    </Menu>
  );
}
